import { promises as fs } from 'fs';
import Link from 'next/link';
import path from 'path';
import { Component, Fragment } from 'react';
import 'reflect-metadata';
import InternalMotiveComponent from '../../components/ui/motive/internal-motive.component';
import { MotiveComponent } from '../../components/ui/motive/motive.component';
import { IOC_TYPES } from '../../context/ioc.types';
import { lazyInject } from '../../context/module';
import { CapabilityService } from '../../services/capability.service';
import { EnvironmentService } from '../../services/environment.service';
import { MotivesService } from '../../services/motives.service';
import { ScriptLoadService } from '../../services/script-load.service';
import { WindowService } from '../../services/window.service';
import { groupBy, includes } from '../../utils/lodash-min';
import { MotiveConfig, MotiveLocationTypes } from '../../utils/request.utils';
import { StringUtils } from '../../utils/string-utils';
import styles from './tools.component.module.scss';
export namespace ToolsTypes {
    export interface DisplayableCategory {
        name: string;
        link: string;
        subcategories: DisplayableSubcategory[];
    }

    export interface DisplayableSubcategory {
        name: string;
        link: string;
        features: DisplayableFeature[];
    }

    export interface DisplayableFeature {
        title: string;
        link: string;
        description: string;
    }
}

interface ToolsComponentState {
    selectedCategory: ToolsTypes.DisplayableCategory;
    selectedSubcategory: ToolsTypes.DisplayableSubcategory;
}

async function getAllFilePaths(postsDirectory: string, urlSet: Set<string>) {
    const paths = await fs.readdir(postsDirectory);
    for (const _path of paths) {
        const fullPath = path.join(postsDirectory, _path);
        const stats = await fs.stat(fullPath);
        if (stats.isDirectory()) {
            getAllFilePaths(fullPath, urlSet);
        } else {
            urlSet.add(fullPath);
        }
    }
}

export async function getStaticPaths() {
    const postsDirectory = path.join(process.cwd(), '/ssr-data/routes/build/data/xconvert.com/tools/')
    const allFilePaths = new Set<string>();
    await getAllFilePaths(postsDirectory, allFilePaths);
    const allowedPaths = [
        new RegExp('tools.*', 'i'),
    ];
    const disallowedList = [
        /root/i,
        /test/i,
        /invalid-path/i
    ];
    const filterFn = (urlPath: string, matchers: RegExp[]) => {
        return matchers.find(p => urlPath.match(p));
    };


    const paths = Array.from(allFilePaths)
        .filter(p => filterFn(p, allowedPaths))
        .filter(p => !filterFn(p, disallowedList))
        .map(path => {
            const urlPath = path.replace(postsDirectory, '').replace('.json', '');
            return { params: { tools: urlPath.replace('tools/', '') } };
        });

    console.log(paths);
    return {
        paths:
            paths,
        fallback: false // See the "fallback" section below
    };
}

export async function getStaticProps({ params }) {
    const props = `/home/user/sites/ps2pdf/ps2pdf-web/ssr-data/routes/build/data/xconvert.com/tools/${params.tools}.json`;
    const data = await fs.readFile(props);
    const jObj = JSON.parse(data.toString());
    // Pass post data to the page via props
    return { props: jObj };
}

export default class ToolsComponent extends Component<any, ToolsComponentState> {
    effectiveMotives: MotiveConfig;
    internalAdsEnabled: boolean;
    isDomainSupportOfflineProcessing: boolean;

    featuredItems: ToolsTypes.DisplayableFeature[];
    featureCategories: ToolsTypes.DisplayableCategory[];
    shouldDisplayAllOptions = true;

    @lazyInject(IOC_TYPES.CapabilityService) capabilityService: CapabilityService;
    @lazyInject(IOC_TYPES.EnvironmentService) envService: EnvironmentService;
    @lazyInject(IOC_TYPES.WindowService) public windowService: WindowService;
    @lazyInject(IOC_TYPES.ScriptLoadService) private scriptLoader: ScriptLoadService;
    @lazyInject(IOC_TYPES.MotivesService) private motivesService: MotivesService;

    readonly state: Readonly<ToolsComponentState> = this.initializeFeatureDataAndGetState();
    constructor(props) {
        super(props);
        this.motivesService.onEffectiveMotiveSettingsChange.subscribe(
            (motives) => {
                this.effectiveMotives = motives;
                this.internalAdsEnabled = this.effectiveMotives && this.effectiveMotives.internalMotivesEnabled;
            },
            (error) => console.error(error),
        );
    }

    initializeFeatureDataAndGetState(): ToolsComponentState {
        this.updateFeatureCategoriesWithChildren();
        return {
            selectedCategory: this.featureCategories[0],
            selectedSubcategory: this.featureCategories[0].subcategories[0],
        };
    }

    updateFeatureCategoriesWithChildren() {
        this.featureCategories = [];
        const converters = this.capabilityService.getConverters();
        const convertersGroupedByType = groupBy(converters, (item) => item.type);

        const displayableCategoryName = (type) => {
            if (includes(['encode', 'decode',], type)) {
                return StringUtils.toTitleCase(type);
            }

            if (includes(['count'], type)) {
                return `Word ${StringUtils.toTitleCase(type)} Tools`;
            }

            if (includes(['meme'], type)) {
                return `${StringUtils.toTitleCase(type)} Tools`;
            }

            return `${StringUtils.toTitleCase(type)} Tools`;
        }
        const pathParts = this.props.path.split('/');
        const lastUrlPart = pathParts[pathParts.length - 1];
        this.shouldDisplayAllOptions = lastUrlPart === 'tools';

        if (this.shouldDisplayAllOptions) {
            this.featureCategories.push({
                name: 'All',
                link: `/tools`,
                subcategories: [
                    {
                        name: 'All',
                        link: `/tools`,
                        features: [],
                    }
                ],
            });
        }

        Object.keys(convertersGroupedByType).filter(type => {
            if (this.shouldDisplayAllOptions) {
                return true;
            }
            return type === lastUrlPart;
        }).forEach(type => {
            const featureCategory: ToolsTypes.DisplayableCategory = {
                name: displayableCategoryName(type),
                link: `/tools/${type}`,
                subcategories: [
                    {
                        name: 'All',
                        link: `/tools`,
                        features: [],
                    }
                ],
            }
            this.featureCategories.push(featureCategory);

            const convertersForType = convertersGroupedByType[type];
            const converterTypesGroupedByToType = groupBy(convertersForType, (item) => item.from);
            Object.keys(converterTypesGroupedByToType).forEach(to => {
                const featureSubcategory: ToolsTypes.DisplayableSubcategory = {
                    name: StringUtils.toTitleCase(to),
                    link: `/tools/${type}-${to}`,
                    features: [],
                }
                featureCategory.subcategories.push(featureSubcategory);
                const converters = converterTypesGroupedByToType[to];
                converters.forEach(converter => {
                    const feature: ToolsTypes.DisplayableFeature = {
                        title: converter.title,
                        description: converter.description,
                        link: converter.link,
                    }
                    featureSubcategory.features.push(feature);
                    featureCategory.subcategories[0].features.push(feature);
                    this.featureCategories[0].subcategories[0].features.push(feature);
                })
            });
        });

    }



    onCategorySelect(selectedCategory: ToolsTypes.DisplayableCategory) {
        this.setState({
            selectedCategory: selectedCategory,
            selectedSubcategory: selectedCategory.subcategories[0],
        })
    }

    onSubcategorySelect(selectedSubcategory: ToolsTypes.DisplayableSubcategory) {
        this.setState({
            selectedSubcategory: selectedSubcategory,
        })
    }

    render() {
        return <div className="pe-2 ps-2">
            <div className="gcontent-container">
                <MotiveComponent
                    data="7590989337" containerWidth="100%" containerHeight="90px"
                    location={MotiveLocationTypes.homePageTop}
                    adWidth="970px"
                    adHeight="90px" showAds={true} className='width_major'
                >
                </MotiveComponent>
                <InternalMotiveComponent
                    internalMotiveId="728"
                    containerWidth="100%" containerHeight="90px" adWidth="970px" adHeight="90px" showAds={true}
                ></InternalMotiveComponent>
            </div>

            <div className={styles['sections']}>
                <section className={styles['all-features']}>
                    {
                        this.shouldDisplayAllOptions && <Fragment>
                            <h2>All Categories</h2>
                            <div className={styles['feature-categories']}>
                                {
                                    this.featureCategories.map((featureCategory, index) => {
                                        return <div key={`feature-category-${index}`} className={styles['feature-category']}>
                                            <Link href={featureCategory.link} className={styles['feature-category-title']} onClick={() => this.onCategorySelect(featureCategory)}>
                                                {featureCategory.name}
                                            </Link>
                                            <span className={styles['feature-category-content']} onClick={() => this.onCategorySelect(featureCategory)}>filter</span>
                                        </div>
                                    })
                                }
                            </div>
                        </Fragment>
                    }
                    {
                        !this.shouldDisplayAllOptions && <h2>{this.featureCategories[0].name}</h2>
                    }
                    {
                        this.state.selectedCategory && this.state.selectedCategory.subcategories.length > 2 && <div className={styles['feature-subcategories']}>
                            {
                                this.state.selectedCategory.subcategories.map((subcategory, index) => {
                                    return <span key={`feature-subcategory-${index}`} className={styles['feature-subcategory']} onClick={() => this.onSubcategorySelect(subcategory)}>
                                        <span className={styles['feature-subcategory-title']}>{subcategory.name}</span>
                                    </span>
                                })
                            }
                        </div>
                    }
                </section>


                {
                    this.state.selectedSubcategory && <section >
                        <h2>{this.state.selectedSubcategory.name === 'All' ? 'All Features' : 'Filtered Features'}</h2>
                        <div className={styles['featured-items']}>
                            {
                                this.state.selectedSubcategory.features.map((feature, index) => {
                                    return <div key={`featured-item-${index}`} className={styles['featured-item']}>
                                        <Link href={feature.link} className={styles['featured-item-title']}>
                                            {feature.title}
                                        </Link>
                                        <div className={styles['featured-item-description']}>{feature.description}</div>
                                    </div>
                                })
                            }
                        </div>
                    </section>
                }
            </div>
        </div >
    }
}
